import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import { decodeText } from '@aytm/app/stats/lib/helpers'

const COLUMN_GAP = 10
const COLUMN_PADDING = 30

const presetSortFunctions = {
  longest: {
    items: (a, b) => b.quote.length - a.quote.length,
    columns: (a, b) => Math.max(...b.items.map(v => v.quote.length)) - Math.max(...a.items.map(v => v.quote.length))
  },
  shortest: {
    items: (a, b) => a.quote.length - b.quote.length,
    columns: (a, b) => Math.min(...a.items.map(v => v.quote.length)) - Math.min(...b.items.map(v => v.quote.length))
  },
  median: {
    items: (a, b) => a.index - b.index,
    columns: (a, b) => Math.max(...a.items.map(v => v.index)) - Math.max(...b.items.map(v => v.index))
  }
}

const calcHeight = items => items.reduce((acc, { height }) => acc + height, 0) + COLUMN_GAP * (items.length - 1)

const calcColumns = (quotes, columns) => {
  if (quotes.length === 0) return columns

  const [first, ...rest] = quotes
  const newColumns = columns.map((col, i) => (i === 0 ? [...col, first] : col))
  newColumns.sort((a, b) => calcHeight(a) - calcHeight(b))

  return calcColumns(rest, newColumns)
}

const measureQuoteHeight = (text, width) => {
  const div = document.createElement('div')

  div.style.position = 'absolute'
  div.style.left = '-1000px'
  div.style.top = '-1000px'
  div.style.width = `${width}px`
  div.style.fontSize = '14px'
  div.style.lineHeight = '16px'
  div.style.fontStyle = 'italic'
  div.style.overflow = 'hidden'
  div.style.overflowWrap = 'break-word'
  div.innerHTML = text || '&nbsp;'

  document.body.appendChild(div)
  const height = div.clientHeight + 48
  document.body.removeChild(div)

  return height
}

const VerbatimQuote = ({ quote, onCopy, quoteContent }) => {
  const [copied, setCopied] = useState(false)

  const timerRef = useRef(null)

  const handleCopy = useCallback(() => {
    onCopy()

    setCopied(true)

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      setCopied(false)
      timerRef.current = null
    }, 2000)
  }, [onCopy])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <div id={`verbatim-quote-${quote.index}`} className="verbatim-quote">
      {quoteContent
        ? quoteContent(quote)
        : <div className="verbatim-quote-text" dangerouslySetInnerHTML={{ __html: quote.quote }} />
      }
      {quote.audio && (
        <div className="verbatim-quote-download">
          <a
            className="verbatim-quote-copy-text"
            href={quote.audio.url}
            target="_blank"
            rel="noreferrer"
            download
          >
            Download audio
          </a>
          <i className="icon-aytmfd-microphone" />
        </div>
      )}
      <div
        className={`verbatim-quote-copy${copied ? ' copied' : ''}`}
        onClick={handleCopy}
      >
        <span className="verbatim-quote-copy-text">Copy</span>
        <span className="verbatim-quote-copied-text">Copied!</span>
        <i className="icon-aytmfd-copy" />
      </div>
    </div>
  )
}

VerbatimQuote.propTypes = {
  quote: PropTypes.shape({
    index: PropTypes.number.isRequired,
    quote: PropTypes.string.isRequired,
    audio: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired,
  onCopy: PropTypes.func.isRequired,
  quoteContent: PropTypes.func
}

const OrderSummary = ({ order }) => {
  if (!order || !order.length) return null

  return (
    <div className="verbatim-quote-note">
      {order.map((item, i) => {
        const name = item.text || item.name || ''
        const qty = item.qty || item.quantity || 0
        const unit = qty === 1 ? 'pc' : 'pcs'

        // one order item per line — matches the platform Comments Page summary
        // name is encoded at rest; innerHTML decodes once (decodeText here would double-decode)
        return (
          <div key={`${name}-${i}`}>
            <span dangerouslySetInnerHTML={{ __html: name }} />
            {` – ${qty} ${unit}`}
          </div>
        )
      })}
    </div>
  )
}

const MNVerbatimView = ({ answer, config, width }) => {
  const verbatim = answer?.verbatim || []
  const preset = config?.preset || 'median'

  const quoteContent = (quote) => {
    const v = verbatim[quote.index]
    if (!v) return null

    return (
      <>
        <OrderSummary order={v.order} />
        <div className="verbatim-quote-text" dangerouslySetInnerHTML={{ __html: `&quot;${v.text || ''}&quot;` }} />
      </>
    )
  }

  const columns = useMemo(() => {
    if (!verbatim.length || !width) return []

    const quotesNumber = verbatim.length
    const columnsNumber = Math.min(3, quotesNumber)

    const w = width - COLUMN_GAP * (columnsNumber - 1) - 24
    const quoteWidth = (w - 2 * COLUMN_PADDING * columnsNumber) / columnsNumber

    const quotes = verbatim
      .map((quote, index) => {
        if (!quote) return null

        const quoteText = `&quot;${quote.text || ''}&quot;`
        const height = measureQuoteHeight(quoteText, quoteWidth)

        return {
          index,
          quote: quoteText,
          height,
          audio: quote.audio
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.quote.length - b.quote.length)

    let resultColumns
    if (3 < quotes.length) {
      const emptyColumns = Array.from({ length: columnsNumber }, () => [])
      resultColumns = calcColumns(quotes, emptyColumns)
    } else {
      resultColumns = quotes.map(quote => [quote])
    }

    resultColumns = resultColumns.map(column => ({
      items: column,
      height: calcHeight(column)
    }))

    resultColumns.sort((a, b) => b.height - a.height)

    const maxColumnHeight = Math.max(...resultColumns.map(column => column.height))

    resultColumns = resultColumns.reduce((acc, cur, i) => {
      const rest = 100 - acc.reduce((sum, v) => sum + v.width, 0)
      const d = (maxColumnHeight - cur.height) / maxColumnHeight
      const colWidth = columnsNumber - i > 1 ? (rest * (1 - d / 3)) / (columnsNumber - i) : rest

      acc.push({ items: cur.items, width: colWidth })
      return acc
    }, [])

    if (Object.hasOwn(presetSortFunctions, preset)) {
      resultColumns.forEach(column => column.items.sort(presetSortFunctions[preset].items))
      resultColumns.sort(presetSortFunctions[preset].columns)
    }

    return resultColumns
  }, [width, verbatim, preset])

  const handleCopy = useCallback(
    index => {
      const text = decodeText(verbatim[index].text)

      if (navigator.clipboard) {
        navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text

        document.body.appendChild(textarea)
        textarea.select()
        textarea.setSelectionRange(0, 99999)
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
    },
    [verbatim]
  )

  return (
    <div className="verbatim-view">
      {0 < columns.length ? (
        columns.map((column, columnIndex) => (
          <div key={columnIndex} className="verbatim-column" style={{ width: `${column.width}%` }}>
            {column.items.map((item) => (
              <VerbatimQuote key={item.index} quote={item} onCopy={() => handleCopy(item.index)} quoteContent={quoteContent} />
            ))}
          </div>
        ))
      ) : (
        <div className="verbatim-empty">nothing found for your search criteria</div>
      )}
    </div>
  )
}

MNVerbatimView.propTypes = {
  width: PropTypes.number,
  answer: PropTypes.shape({
    verbatim: PropTypes.array
  }),
  config: PropTypes.shape({
    preset: PropTypes.oneOf(['longest', 'median', 'shortest'])
  })
}

MNVerbatimView.displayName = 'MNVerbatimView'

export default MNVerbatimView
